/**
 * Centralized LLM model definitions and helper functions
 *
 * To add a new provider or model, simply update the LLM_MODELS array below.
 * The rest of the application will automatically pick up the changes.
 */

// Type definitions
export type LLMProviderKey = 'openai' | 'gemini' | 'together';
export type CostLevel = 'low' | 'medium' | 'high';
export type SpeedLevel = 'very_fast' | 'fast' | 'moderate' | 'slow';

export interface LLMModelDefinition {
  id: string;                    // Model identifier used in API calls
  provider: LLMProviderKey;      // Provider key
  label: string;                 // Human-readable display name
  description: string;           // Short description of the model
  cost: CostLevel;               // Cost tier
  speed: SpeedLevel;             // Speed tier
  isDefault?: boolean;           // Default model for this provider
  contextWindow?: number;        // Context window size (optional)
}

// Provider display names
export const LLM_PROVIDER_NAMES: Record<LLMProviderKey, string> = {
  openai: 'OpenAI',
  gemini: 'Google Gemini',
  together: 'Together AI',
};

/**
 * All available LLM models organized by provider
 *
 * To add new models:
 * 1. Add the model definition to this array
 * 2. Set isDefault: true for the recommended default model per provider
 */
export const LLM_MODELS: LLMModelDefinition[] = [
  // OpenAI Models
  {
    id: 'gpt-4.1',
    provider: 'openai',
    label: 'GPT-4.1',
    description: 'Flagship model with best performance',
    cost: 'high',
    speed: 'moderate',
    isDefault: true,
  },
  {
    id: 'gpt-4.1-mini',
    provider: 'openai',
    label: 'GPT-4.1 Mini',
    description: 'Smaller, faster, 83% cheaper than GPT-4o',
    cost: 'medium',
    speed: 'fast',
  },
  {
    id: 'gpt-4.1-nano',
    provider: 'openai',
    label: 'GPT-4.1 Nano',
    description: 'Fastest and most cost-effective',
    cost: 'low',
    speed: 'very_fast',
  },
  // Gemini Models
  {
    id: 'gemini-2.5-flash',
    provider: 'gemini',
    label: 'Gemini 2.5 Flash',
    description: 'Best price-performance ratio',
    cost: 'medium',
    speed: 'fast',
    contextWindow: 1000000,
  },
  {
    id: 'gemini-2.5-flash-lite',
    provider: 'gemini',
    label: 'Gemini 2.5 Flash Lite',
    description: 'Cost-efficient, high throughput',
    cost: 'low',
    speed: 'very_fast',
    contextWindow: 1000000,
  },
    {
    id: 'gemini-3.1-flash-lite',
    provider: 'gemini',
    label: 'Gemini 3.1 Flash Lite',
    description: '2.5x faster than 2.5 Flash, best value',
    cost: 'low',
    speed: 'very_fast',
    contextWindow: 1000000,
  },
  {
    id: 'gemini-3.5-flash-lite',
    provider: 'gemini',
    label: 'Gemini 3.5 Flash Lite',
    description: 'Fastest, lowest-cost 3.5 model; best value for extraction',
    cost: 'low',
    speed: 'very_fast',
    contextWindow: 1000000,
  },
  {
    id: 'gemini-3.6-flash',
    provider: 'gemini',
    label: 'Gemini 3.6 Flash',
    description: 'Better token efficiency and agentic planning at lower cost than 3.5 Flash',
    cost: 'medium',
    speed: 'fast',
    contextWindow: 1000000,
  },
  {
    id: 'gemini-3.7-flash',
    provider: 'gemini',
    label: 'Gemini 3.7 Flash',
    description: 'Improved agentic planning and instruction-following over 3.6 Flash',
    cost: 'medium',
    speed: 'fast',
    contextWindow: 1000000,
  },
  {
    id: 'gemini-3.8-flash',
    provider: 'gemini',
    label: 'Gemini 3.8 Flash',
    description: 'Latest Flash: stronger multi-step reasoning and agentic tool use than 3.7 Flash',
    cost: 'medium',
    speed: 'fast',
    isDefault: true,
    contextWindow: 1000000,
  },
  {
    id: 'gemini-3.5-flash',
    provider: 'gemini',
    label: 'Gemini 3.5 Flash',
    description: 'Frontier intelligence for schema discovery and agentic tasks',
    cost: 'medium',
    speed: 'fast',
    contextWindow: 1000000,
  },
  {
    id: 'gemini-3.1-pro-preview',
    provider: 'gemini',
    label: 'Gemini 3.1 Pro (Preview)',
    description: 'Most capable reasoning model',
    cost: 'high',
    speed: 'moderate',
    contextWindow: 1000000,
  },
  // Together AI Models
  // Add models here when available
];

// ── Default model constants ─────────────────────────────────────────
export const DEFAULT_SCHEMA_MODEL = 'gemini-3.8-flash';
export const DEFAULT_EXTRACTION_MODEL = 'gemini-3.5-flash-lite';
export const DEFAULT_RELEASE_EXTRACTION_MODEL = 'gemini-3.5-flash-lite';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get all models for a specific provider
 */
export function getModelsForProvider(provider: LLMProviderKey): LLMModelDefinition[] {
  return LLM_MODELS.filter(m => m.provider === provider);
}

/**
 * Get the default model ID for a provider
 * Returns the model marked as isDefault, or the first model if none is marked
 */
export function getDefaultModelForProvider(provider: LLMProviderKey): string {
  const models = getModelsForProvider(provider);
  const defaultModel = models.find(m => m.isDefault);
  return defaultModel?.id || models[0]?.id || '';
}

/**
 * Find a model by its ID (across all providers)
 */
export function getModelById(modelId: string): LLMModelDefinition | undefined {
  return LLM_MODELS.find(m => m.id === modelId);
}

/**
 * Find a model by provider and model ID
 */
export function getModelByProviderAndId(
  provider: LLMProviderKey,
  modelId: string
): LLMModelDefinition | undefined {
  return LLM_MODELS.find(m => m.provider === provider && m.id === modelId);
}

/**
 * Check if a provider has any models defined
 */
export function hasModelsForProvider(provider: LLMProviderKey): boolean {
  return getModelsForProvider(provider).length > 0;
}

/**
 * Filter configured providers to only those with available models
 * Use this to hide providers like Together AI that don't have models in the allowed list
 */
export function getAvailableProviders(
  configuredProviders: string[]
): LLMProviderKey[] {
  return configuredProviders.filter(
    p => hasModelsForProvider(p as LLMProviderKey)
  ) as LLMProviderKey[];
}

// ============================================================================
// UI Helper Functions
// ============================================================================

/**
 * Get badge variant for cost level
 */
export function getCostBadgeVariant(cost: CostLevel): 'success' | 'warning' | 'destructive' | 'secondary' {
  switch (cost) {
    case 'low': return 'success';
    case 'medium': return 'warning';
    case 'high': return 'destructive';
    default: return 'secondary';
  }
}

/**
 * Get badge variant for speed level
 */
export function getSpeedBadgeVariant(speed: SpeedLevel): 'success' | 'info' | 'warning' | 'secondary' {
  switch (speed) {
    case 'very_fast': return 'success';
    case 'fast': return 'info';
    case 'moderate': return 'warning';
    case 'slow': return 'warning';
    default: return 'secondary';
  }
}

/**
 * Format cost level for display
 */
export function formatCostLevel(cost: CostLevel): string {
  return cost.charAt(0).toUpperCase() + cost.slice(1);
}

/**
 * Format speed level for display
 */
export function formatSpeedLevel(speed: SpeedLevel): string {
  return speed.split('_').map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');
}
